import { useState } from "react";
import subscriptionsService from "../../services/subscriptions.service";

import { FormWarning, Loader } from "@shltelemedicine/shl-ui-lib";
import OpenReport from "../Shared/OpenReport/OpenReport";
import ViewFileForm, {
  ViewFileFormInputs,
} from "../Shared/ViewFile/ViewFileForm/ViewFileForm";
import authService from "../../services/authentication.service";
import { FormattedMessage } from "react-intl";
import { LoginFailureReason } from "../../models/results/login-result.interface";

type FormattedMessageType = { id: string; defaultMessage: string };

const genericErrorMessage: FormattedMessageType = {
  id: "ERROR.SOMETHING_WENT_WRONG",
  defaultMessage: "Something went wrong",
};

const getAuthFailureReasonMessage = (
  failureReason: LoginFailureReason
): FormattedMessageType => {
  switch (failureReason) {
    case LoginFailureReason.InvalidCredentials:
      return {
        id: "LOGIN.INVALID_CREDENTIALS",
        defaultMessage:
          "Invalid User Name or Password.<br></br>Please check your credentials and try again",
      };
    case LoginFailureReason.Blocked:
      return {
        id: "LOGIN.BLOCKED",
        defaultMessage:
          "You are not authorized to access this portal.<br></br>Contact your system administrator.",
      };
    default:
      return genericErrorMessage;
  }
};

type ViewEcgProps = { token?: string };

const ViewPrescription = ({ token }: ViewEcgProps) => {
  const [loading, setLoading] = useState(false);
  const [isErr, setIsErr] = useState(false);
  const [errorFromServer, setErrorFromServer] = useState("");

  const [errorMessage, setErrorMessage] = useState<FormattedMessageType>(
    genericErrorMessage
  );
  const [pdfFile, setPdfFile] = useState<Blob>();

  const viewPrescriptionHandler = async ({
    username,
    password,
  }: ViewFileFormInputs) => {
    if (!token) {
      setIsErr(true);
      return;
    }

    try {
      setLoading(true);

      const {
        success: authSuccess,
        data: authToken,
        message: failureReasonCode,
      } = await authService.authenticateApiUser(username, password);

      setIsErr(!authSuccess);
      if (!authSuccess || !authToken) {
        setErrorMessage(
          failureReasonCode
            ? getAuthFailureReasonMessage(
                +failureReasonCode as LoginFailureReason
              )
            : genericErrorMessage
        );
        return;
      }

      const {
        success: getPdfSuccess,
        data: prescriptionPdf,
        message: getPdfMessage,
      } = await subscriptionsService.downloadPrescriptionPdfByToken({
        pdfToken: token,
        authToken,
      });

      setIsErr(!getPdfSuccess);

      if (!getPdfSuccess || !prescriptionPdf) {
        setErrorFromServer(getPdfMessage || genericErrorMessage.defaultMessage);
        return;
      }

      setPdfFile(prescriptionPdf);
    } finally {
      setLoading(false);
    }
  };

  const generateFormMessage = () => {
    const isMessageFromServer = !!errorFromServer;
    if (isMessageFromServer) return errorFromServer;
    if (isErr)
      return (
        <FormattedMessage
          id={errorMessage.id}
          defaultMessage={errorMessage.defaultMessage}
          values={{
            br: () => <br></br>,
          }}
        />
      );
    return (
      <FormattedMessage
        id={"VIEW_PRESCRIPTION.MESSAGE"}
        defaultMessage={
          "Insert username and password to view the prescription request"
        }
        values={{
          br: () => <br></br>,
        }}
      />
    );
  };

  return pdfFile ? (
    <OpenReport
      file={pdfFile}
      titleStringId="VIEW_PRESCRIPTION.REPORT_CREATED"
      linkStringId="VIEW_PRESCRIPTION.VIEW_REPORT"
    />
  ) : (
    <>
      {loading ? (
        <div className="mb-3">
          <Loader />
        </div>
      ) : (
        <FormWarning
          message={generateFormMessage()}
          className={`${!isErr ? "app-hint" : ""} mb-3 px-3 font-md`}
        />
      )}
      <ViewFileForm onSubmit={viewPrescriptionHandler} />
    </>
  );
};

export default ViewPrescription;
